import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Card, CardContent } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Switch } from "../../../ui/switch";
import { Tooltip } from "../../../ui/tooltip";
import { Collapsible, CollapsibleTrigger } from "../../../ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../ui/alert-dialog";
import { 
  Trash2,
  HelpCircle,
  Tag as TagIcon,
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import TruncatedText from "./TruncatedText";
import { ConfigSidebarState, InterjectionGroup, InterjectionContent } from "../utils/types";

interface PlaceholdersTabProps {
  nodes: any[];
  globalVariableRegistry: any;
  decisionVariablesRegistry: any;
  onUpdateGlobalVariableRegistry: (registry: any) => void;
  onNodeFocus?: (nodeId: string) => void;
  configState?: ConfigSidebarState;
}

const PlaceholdersTab: React.FC<PlaceholdersTabProps> = ({
  nodes,
  globalVariableRegistry,
  decisionVariablesRegistry,
  onUpdateGlobalVariableRegistry,
  onNodeFocus,
  configState,
}) => {
  const [editingPlaceholder, setEditingPlaceholder] = useState<string | null>(null);
  const [newExampleValue, setNewExampleValue] = useState("");
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [placeholderToDelete, setPlaceholderToDelete] = useState<string | null>(null);

  // Memoize placeholder computation to avoid O(N) traversal on every render
  const allPlaceholders = useMemo(() => {
    const placeholders: Record<string, any> = { ...globalVariableRegistry };
    
    // Scan all nodes for placeholders that might not be in the global registry
    nodes.forEach((node: any) => {
      const content = node.data?.content || "";
      const regex = /{([^{}]+)}/g;
      let match;
      while ((match = regex.exec(content)) !== null) {
        const placeholderName = match[1];
        if (!placeholders[placeholderName]) {
          // Add placeholder that exists in nodes but not in global registry
          placeholders[placeholderName] = {
            name: placeholderName,
            values: [], // Changed from examples to values
            persist: false // Default persist to false
          };
        }
      }
      
      // Also check tool call parameters and response variables
      if (node.data?.toolCalls) {
        node.data.toolCalls.forEach((toolCall: any) => {
          // Check parameters for placeholders
          if (Array.isArray(toolCall.parameters)) {
            toolCall.parameters.forEach((param: any) => {
            if (param.value) {
              const toolCallRegex = /{([^{}]+)}/g;
              let tcMatch;
              while ((tcMatch = toolCallRegex.exec(param.value)) !== null) {
                const placeholderName = tcMatch[1];
                if (!placeholders[placeholderName] && !decisionVariablesRegistry[placeholderName]) {
                  // Only add if it's not already a decision variable
                  placeholders[placeholderName] = {
                    name: placeholderName,
                    values: [],
                    persist: true // Tool call parameters should persist by default
                  };
                }
              }
            }
          });
          }
          
          // Include response variables as placeholders
          if (Array.isArray(toolCall.responseVariables)) {
            toolCall.responseVariables.forEach((responseVar: any) => {
            if (responseVar.name && !placeholders[responseVar.name] && !decisionVariablesRegistry[responseVar.name]) {
              // Only add if it's not already a decision variable
              placeholders[responseVar.name] = {
                name: responseVar.name,
                values: responseVar.values || [],
                persist: false
              };
            }
          });
          }
        });
      }
    });
    
    // Scan interjection groups for placeholders
    if (configState?.groups) {
      configState.groups.forEach((group: InterjectionGroup) => {
        group.content.forEach((content: InterjectionContent) => {
          // Check interjection_text for placeholders
          if (content.interjection_text) {
            const regex = /{([^{}]+)}/g;
            let match;
            while ((match = regex.exec(content.interjection_text)) !== null) {
              const placeholderName = match[1];
              if (!placeholders[placeholderName]) {
                placeholders[placeholderName] = {
                  name: placeholderName,
                  values: [],
                  persist: false
                };
              }
            }
          }
          
          // Check interjection_response for placeholders
          if (content.interjection_response) {
            const regex = /{([^{}]+)}/g;
            let match;
            while ((match = regex.exec(content.interjection_response)) !== null) {
              const placeholderName = match[1];
              if (!placeholders[placeholderName]) {
                placeholders[placeholderName] = {
                  name: placeholderName,
                  values: [],
                  persist: false
                };
              }
            }
          }
        });
      });
    }
    
    return placeholders;
  }, [nodes, globalVariableRegistry, configState, decisionVariablesRegistry]);

  const handleDeletePlaceholder = (placeholderName: string) => {
    const updatedRegistry = { ...globalVariableRegistry };
    delete updatedRegistry[placeholderName];
    onUpdateGlobalVariableRegistry(updatedRegistry);
    setPlaceholderToDelete(null);
  };

  const handleAddExample = (placeholderName: string) => {
    const trimmedValue = newExampleValue.trim();
    if (!trimmedValue) return;
    
    const currentValues = globalVariableRegistry[placeholderName]?.values || [];
    
    // Check if example already exists to prevent duplicates
    if (currentValues.includes(trimmedValue)) {
      setNewExampleValue("");
      return;
    }
    
    const updatedRegistry = {
      ...globalVariableRegistry,
      [placeholderName]: {
        ...globalVariableRegistry[placeholderName],
        values: [...currentValues, trimmedValue] // Changed from examples to values
      }
    };
    
    onUpdateGlobalVariableRegistry(updatedRegistry);
    setNewExampleValue("");
  };

  const handleRemoveExample = (placeholderName: string, indexToRemove: number) => {
    const updatedRegistry = {
      ...globalVariableRegistry,
      [placeholderName]: {
        ...globalVariableRegistry[placeholderName],
        values: globalVariableRegistry[placeholderName]?.values?.filter((_: string, index: number) => index !== indexToRemove) || []
      }
    };
    
    onUpdateGlobalVariableRegistry(updatedRegistry);
  };

  // Handle persist toggle
  const handlePersistToggle = (placeholderName: string, persist: boolean) => {
    const updatedRegistry = {
      ...globalVariableRegistry,
      [placeholderName]: {
        ...globalVariableRegistry[placeholderName],
        persist
      }
    };
    
    onUpdateGlobalVariableRegistry(updatedRegistry);
  };

  // Function to find which nodes use a specific placeholder
  const getNodesUsingPlaceholder = (placeholderName: string) => {
    return nodes.filter((node: any) => {
      // Check content
      const content = node.data?.content || "";
      if (content.includes(`{${placeholderName}}`)) {
        return true;
      }
      
      // Check tool call parameters
      if (node.data?.toolCalls) {
        for (const toolCall of node.data.toolCalls) {
          if (Array.isArray(toolCall.parameters)) {
            for (const param of toolCall.parameters) {
              if (typeof param.value === "string" && param.value.includes(`{${placeholderName}}`)) {
                return true;
              }
            }
          }
        }
      }
      
      return false;
    });
  };

  // Function to find which interjection groups use a specific placeholder
  const getInterjectionsUsingPlaceholder = (placeholderName: string) => {
    if (!configState?.groups) return [];
    
    const usedInInterjections: { group: InterjectionGroup; content: InterjectionContent; field: "text" | "response" }[] = [];
    
    configState.groups.forEach((group: InterjectionGroup) => {
      group.content.forEach((content: InterjectionContent) => {
        if (content.interjection_text?.includes(`{${placeholderName}}`)) {
          usedInInterjections.push({ group, content, field: "text" });
        }
        if (content.interjection_response?.includes(`{${placeholderName}}`)) {
          usedInInterjections.push({ group, content, field: "response" });
        }
      });
    });
    
    return usedInInterjections;
  };

  // Function to check if a placeholder is used anywhere (for delete button state)
  const isPlaceholderUsed = (placeholderName: string) => {
    const nodesUsing = getNodesUsingPlaceholder(placeholderName);
    const interjectionsUsing = getInterjectionsUsingPlaceholder(placeholderName);
    return nodesUsing.length > 0 || interjectionsUsing.length > 0;
  };

  // Use effect to handle scanning for new placeholders and updating the registry
  useEffect(() => {
    // Check if there are any new placeholders not in the global registry
    const hasNewPlaceholders = Object.keys(allPlaceholders).some(
      key => !globalVariableRegistry[key]
    );
    
    if (hasNewPlaceholders) {
      onUpdateGlobalVariableRegistry(allPlaceholders);
    }
  }, [allPlaceholders, globalVariableRegistry, onUpdateGlobalVariableRegistry]);

  return (
    <div className="h-[calc(100vh-160px)] overflow-y-auto overflow-x-hidden p-6">
      {/* Existing Placeholders */}
      <div>
        {(() => {
          if (Object.keys(allPlaceholders).length === 0) {
            return (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="space-y-3">
                  <TagIcon className="w-12 h-12 text-muted-foreground mx-auto" />
                  <h3 className="text-lg font-gc-med text-foreground">
                    No placeholders found
                  </h3>
                  <p className="text-muted-foreground text-sm max-w-md">
                    Add placeholders like {"{name}"} to your node content to see them here
                  </p>
                </div>
              </div>
            );
          }
          
          return Object.entries(allPlaceholders).map(([placeholderName, placeholder]: [string, any]) => {
            const nodesUsing = getNodesUsingPlaceholder(placeholderName);
            const interjectionsUsing = getInterjectionsUsingPlaceholder(placeholderName);
            const isUsed = isPlaceholderUsed(placeholderName);
            const persist = placeholder.persist || false; // Default to false if not set
            
            const isExpanded = expandedNodes.includes(`nodes-${placeholderName}`);
            
            return (
              <div key={placeholderName}>
                <Card className="mb-4 border-border">
                  <CardContent className="p-3">
                    {/* Header row with placeholder name and controls */}
                    <div className="flex items-center justify-between mb-3">
                      {/* Left: Placeholder name and controls */}
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="secondary" 
                          className="text-xs font-gc-med"
                        >
                          <TruncatedText text={placeholderName} maxWidth={200} tooltip={false} />
                        </Badge>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">Persist</span>
                          <Switch 
                            checked={persist}
                            onCheckedChange={(checked) => handlePersistToggle(placeholderName, checked)}
                          />
                          <Tooltip content="When enabled, this variable will keep the same value throughout the entire conversation. When disabled, the value can change at different points in the conversation." side="bottom" immediate={true}>
                            <div>
                              <HelpCircle className="w-3 h-3 text-maitai-green hover:text-maitai-green/80 transition-colors" />
                            </div>
                          </Tooltip>
                        </div>
                      </div>

                      {/* Right: Delete button and node count dropdown */}
                      <div className="flex items-center gap-1">
                        <AlertDialog open={placeholderToDelete === placeholderName} onOpenChange={(open) => !open && setPlaceholderToDelete(null)}>
                          <AlertDialogTrigger asChild>
                            <div>
                              <Tooltip 
                                content={isUsed ? `Cannot delete: placeholder is used in ${nodesUsing.length} node(s) and ${interjectionsUsing.length} interjection(s)` : "Delete placeholder"}
                                side="left"
                              >
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  disabled={isUsed}
                                  className={cn(
                                    "h-6 w-6 p-0",
                                    isUsed 
                                      ? "text-muted-foreground/50 cursor-not-allowed" 
                                      : "text-muted-foreground hover:text-destructive"
                                  )}
                                  onClick={() => !isUsed && setPlaceholderToDelete(placeholderName)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </Tooltip>
                            </div>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-lg font-gc-med text-foreground">
                                Delete Placeholder
                              </AlertDialogTitle>
                              <AlertDialogDescription className="text-sm font-gc-reg text-foreground">
                                Are you sure you want to delete the placeholder "{placeholderName}"? This action cannot be undone.
                                {isUsed && (
                                  <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded">
                                    <p className="text-destructive font-medium">Warning: This placeholder is currently in use!</p>
                                    <ul className="mt-1 text-xs list-disc list-inside space-y-1">
                                      {nodesUsing.length > 0 && (
                                        <li>Used in {nodesUsing.length} node(s)</li>
                                      )}
                                      {interjectionsUsing.length > 0 && (
                                        <li>Used in {interjectionsUsing.length} interjection content(s)</li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="font-gc-med">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction 
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 font-gc-med"
                                onClick={() => handleDeletePlaceholder(placeholderName)}
                                disabled={isUsed}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        
                        {/* Usage count dropdown */}
                        {(nodesUsing.length > 0 || interjectionsUsing.length > 0) && (
                          <Collapsible
                            open={isExpanded}
                            onOpenChange={(open) => {
                              const key = `nodes-${placeholderName}`;
                              if (open) {
                                setExpandedNodes([...expandedNodes, key]);
                              } else {
                                setExpandedNodes(expandedNodes.filter(k => k !== key));
                              }
                            }}
                          >
                            <CollapsibleTrigger className="!flex !items-center !gap-1 !justify-start !w-auto text-xs text-muted-foreground hover:text-foreground transition-colors [&>svg]:!mr-0">
                              <span className="font-gc-med">{nodesUsing.length + interjectionsUsing.length}</span>
                            </CollapsibleTrigger>
                          </Collapsible>
                        )}
                      </div>
                    </div>

                    {/* Values Section */}
                    <div className="border-t border-border pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-gc-med text-foreground">
                          Values ({placeholder.values?.length || 0})
                        </h4>
                      </div>
                      
                      {/* Add Example Input */}
                      <div className="flex gap-2 mb-2">
                        <Input 
                          value={editingPlaceholder === placeholderName ? newExampleValue : ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setEditingPlaceholder(placeholderName);
                            setNewExampleValue(e.target.value);
                          }}
                          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && handleAddExample(placeholderName)}
                          placeholder="Add example value"
                          className="flex-1 h-6 text-xs"
                        />
                        <Button 
                          onClick={() => handleAddExample(placeholderName)}
                          disabled={!newExampleValue.trim() || editingPlaceholder !== placeholderName}
                          size="sm"
                          className="h-6 px-2 text-xs bg-foreground hover:bg-foreground/90 text-background"
                        >
                          Add
                        </Button>
                      </div>
                      
                      {/* Example Tags */}
                      <div className="max-h-20 overflow-auto">
                        {placeholder.values?.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            No values yet. Add some example values for this placeholder.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {placeholder.values?.map((value: string, index: number) => (
                              <Badge 
                                key={`${value}-${index}`}
                                variant="outline"
                                className="text-xs border-maitai-green/30 text-maitai-green bg-maitai-green/10 hover:bg-maitai-green/20 transition-colors"
                              >
                                {value}
                                <button
                                  onClick={() => handleRemoveExample(placeholderName, index)}
                                  className="ml-1 hover:text-destructive transition-colors"
                                >
                                  ×
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded usage content */}
                    {(nodesUsing.length > 0 || interjectionsUsing.length > 0) && isExpanded && (
                      <div className="border-t border-border pt-3 mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-gc-med text-foreground">
                            Used in {nodesUsing.length + interjectionsUsing.length} location{(nodesUsing.length + interjectionsUsing.length) !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="max-h-32 overflow-auto space-y-1">
                          {/* Node usage */}
                          {nodesUsing.map((node: any, index: number) => (
                            <Tooltip key={`node-${node.id}`} content="Click to focus on this node" side="left">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onNodeFocus?.(node.id)}
                                className="w-full justify-start text-left h-auto p-2 border border-border/50 bg-accent/20 hover:bg-accent/40"
                              >
                                <span className="font-gc-med text-maitai-green">Node #{index + 1}</span>
                                <span className="text-foreground ml-2">
                                  {node.data?.identifier || "Unclassified"}
                                </span>
                                <span className="text-muted-foreground text-[10px] ml-2">
                                  ({node.data?.entityType || "Unknown"})
                                </span>
                              </Button>
                            </Tooltip>
                          ))}
                          
                          {/* Interjection usage */}
                          {interjectionsUsing.map((usage, index: number) => (
                            <div
                              key={`interjection-${usage.group.id}-${usage.content.id}-${usage.field}`}
                              className="w-full justify-start text-left h-auto p-2 border border-border/50 bg-blue-50/50 rounded-md"
                            >
                              <div className="flex items-center">
                                <span className="font-gc-med text-blue-600">Interjection #{index + 1}</span>
                                <span className="text-foreground ml-2">
                                  {usage.group.groupName || usage.group.type}
                                </span>
                                <span className="text-muted-foreground text-[10px] ml-2">
                                  ({usage.field === "text" ? "Text" : "Response"})
                                </span>
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-1 truncate">
                                {usage.field === "text" ? usage.content.interjection_text : usage.content.interjection_response}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
};

export default PlaceholdersTab; 